export const pct = (v, digits = 0) =>
  v === null || v === undefined ? '--' : `${v > 0 ? '+' : ''}${Number(v).toFixed(digits)}%`;

export const num = (v, digits = 0) =>
  v === null || v === undefined ? '--' : Number(v).toFixed(digits);

export const share = (v) =>
  v === null || v === undefined ? '--' : `${Math.round(Number(v) * 100)}%`;

export const money = (v) => {
  if (v === null || v === undefined) return '--';
  const n = Number(v);
  for (const [cut, suffix] of [[1e12, 'T'], [1e9, 'B'], [1e6, 'M']]) {
    if (Math.abs(n) >= cut) return `$${(n / cut).toFixed(1)}${suffix}`;
  }
  return `$${n.toLocaleString()}`;
};

export const day = (d) =>
  !d
    ? '--'
    : new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });

export const GEO_NAMES = {
  US: 'United States', GB: 'United Kingdom', AU: 'Australia', CA: 'Canada',
  DE: 'Germany', FR: 'France', JP: 'Japan', IN: 'India', CN: 'China',
  BR: 'Brazil', MX: 'Mexico', NL: 'Netherlands', SE: 'Sweden', NO: 'Norway',
  DK: 'Denmark', CH: 'Switzerland', ES: 'Spain', IT: 'Italy', BE: 'Belgium',
  SG: 'Singapore', MY: 'Malaysia', TH: 'Thailand', HK: 'Hong Kong',
  ZA: 'South Africa', SA: 'Saudi Arabia', AE: 'UAE', WORLDWIDE: 'Worldwide',
};

export const geoName = (g) => GEO_NAMES[String(g || '').toUpperCase()] || g || '--';

/* Database enums are snake_case because that is what the schema wants. Nobody
 * should have to read "upstream_need" on a page. */
export const words = (s) => (s === null || s === undefined ? '' : String(s).replace(/_/g, ' '));

/* The models mostly return one unbroken block. Rendered as-is that is a wall
 * nobody finishes, so where the writer gave us no breaks we put them in every
 * third sentence - close enough to where a paragraph would fall, and far better
 * than none. Anything already broken up is left exactly as written. */
const SENTENCES_PER_PARA = 3;

export const paragraphs = (text) => {
  if (!text) return [];
  const given = String(text).split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  if (given.length > 1) return given;

  const body = given[0] || '';
  // A break only counts where a capital follows, which keeps "U.S. sales" and
  // "approx. 4% of revenue" whole. Marked then split rather than a lookbehind,
  // which older Safari refuses to parse at all - and a regex it cannot parse
  // takes the whole module down, not just this line.
  const MARK = '\u0000';
  const sentences = body
    .replace(/([.!?])\s+(?=["'\u201C]?[A-Z])/g, `$1${MARK}`)
    .split(MARK)
    .filter(Boolean);
  if (sentences.length <= SENTENCES_PER_PARA) return [body];

  const out = [];
  for (let i = 0; i < sentences.length; i += SENTENCES_PER_PARA) {
    out.push(sentences.slice(i, i + SENTENCES_PER_PARA).join(' '));
  }
  return out;
};

/* The lifecycle of a trend, in words a reader can act on.
 *
 * The stored names describe the test that was run, not what the reader wants to
 * know, and two of them actively mislead. "confirmed" sounds like a stronger
 * version of "unconfirmed" and is nothing of the sort: it means the break is
 * past its first week but still too young to fit a slope to, because that needs
 * six points (SLOPE_MIN_AGE = 5 in statistical_detection.py). "building" is not
 * the next rung up either - it is the first state where the question "is this
 * still rising?" can be asked at all, and the answer came back yes.
 *
 * So the two are not two grades of the same measurement. One is about age and
 * the other about the shape of the curve. The labels say which. */
export const STATE_COPY = {
  unconfirmed: {
    label: 'new this week',
    note: 'Broke out of its normal range this week. Nothing yet says whether it holds.',
  },
  confirmed: {
    label: 'holding',
    note: 'Has stayed above its normal range for one to four weeks. Too few points so far to tell whether it is still climbing.',
  },
  building: {
    label: 'still rising',
    note: 'Five or more weeks since the break, and the fitted trend through those weeks is still rising.',
  },
  decaying: {
    label: 'rolling over',
    note: 'Five or more weeks in, and the fitted trend has turned decisively down.',
  },
  peaked: {
    label: 'peaked',
    note: 'Past its high but still above its normal range.',
  },
  closed: {
    label: 'closed',
    note: 'Back inside its normal range, or it rolled over. No longer running.',
  },
};

export const stateLabel = (s) => STATE_COPY[s]?.label || words(s);
export const stateNote = (s) => STATE_COPY[s]?.note || null;

/* Which way a trend cuts, which materiality cannot say on its own.
 *
 * Detection fires on rising search interest, and interest rises just as hard
 * for a recall as for a launch - ChloraPrep's biggest week in five years was a
 * nationwide contamination notice, and Encore Boston Harbor's was a strike. A
 * tile showing materiality alone tells a reader something is big while
 * withholding whether it is good news, which is the half that decides what
 * they do about it.
 *
 * Zero is withheld rather than drawn neutral. The model returns it on the rows
 * it also scored materiality zero, so it means "no reading" and not "balanced";
 * a grey chip there would assert a verdict we were never given. */
export const DIRECTION_NOTE =
  'Whether the reason behind the surge helps or hurts revenue, from -10 to +10. '
  + 'Scored separately from materiality, because a recall and a launch both '
  + 'raise search interest.';

export const direction = (v) => {
  if (v === null || v === undefined || Number(v) === 0) return null;
  const n = Number(v);
  const up = n > 0;
  return {
    n,
    cls: up ? 'up' : 'down',
    word: up ? 'Positive' : 'Negative',
    arrow: up ? '\u25B2' : '\u25BC',
    // A true minus sign rather than a hyphen: at 11px in a coloured pill the
    // hyphen reads as a dash between two things.
    label: `${up ? '+' : '\u2212'}${Math.abs(n)}`,
  };
};

/* Why a term has no episode. Five different answers, and the reader is owed the
 * right one: "come back in four months" and "Google has never reported this"
 * are opposite statements about the same flat line. */
export const STATUS_COPY = {
  tested: 'Tested every week. Nothing has broken out of its normal range.',
  short_history:
    'Too new to have a baseline yet. We keep polling it and will test it once there is enough history.',
  too_sparse:
    'Google reports this term too rarely for a baseline to mean anything. More weeks will not fix that.',
  no_recent_data:
    'Google has reported no measurable search volume for this term recently, so there is nothing to test.',
  not_returned: 'We asked for this term and got nothing back. That is our problem, not the market being quiet.',
};

export const coverageNote = (s) => {
  if (!s) return null;
  const base = STATUS_COPY[s.status] || null;
  const { real_weeks: real, weeks_returned: total } = s;
  if (real !== null && real !== undefined && total && real < total) {
    const counts = `Google reported it in ${real} of the last ${total} weeks.`;
    return base ? `${base} ${counts}` : counts;
  }
  return base;
};
